import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Monitor, Tablet, Smartphone, MousePointerClick, Eye, Undo2, RotateCcw, ExternalLink, Check, Paintbrush, Type, EyeOff, LayoutTemplate } from 'lucide-react'
import toast from 'react-hot-toast'
import { tenantAPI, themesAPI, appSettingsAPI } from '../../services/api'
import { useAdminBusiness } from '../../contexts/AdminBusinessContext'
import { servicesOf } from '../../config/services'
import { MENU_TEMPLATES, STORE_TEMPLATES, BOOKING_TEMPLATES } from '../customer/templates/templateMeta'
import { EDITOR_FRAME } from '../../components/site-editor/SiteEdits'
import { t } from '../../i18n'

const EMPTY = { texts: {}, hidden: [], colors: {} }
const COLOR_FIELDS = [['primary', 'Main color', '#1d4ed8'], ['accent', 'Accent color', '#f59e0b'], ['background', 'Page background', '#ffffff'], ['text', 'Text color', '#111827']]
const TEMPLATE_FIELDS = [
  ['menu', 'menu_template', 'Menu layout', MENU_TEMPLATES],
  ['store', 'store_template', 'Store layout', STORE_TEMPLATES],
  ['bookings', 'booking_template', 'Booking page layout', BOOKING_TEMPLATES],
]
const DEVICES = [['desktop', Monitor, '100%'], ['tablet', Tablet, '820px'], ['mobile', Smartphone, '390px']]
const sameItem = (a, b) => a.tag === b.tag && a.cls === b.cls && a.text === b.text && a.src === b.src

// Website builder: the real website in a frame, where the owner clicks any
// text to change it or hides any item, picks colors and layouts, and sees it
// live. Nothing goes public until "Publish".
export default function SiteEditorPage() {
  const { branch, businessType } = useAdminBusiness()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const frameRef = useRef(null)
  const services = servicesOf(branch?.services, businessType)

  const [saved, setSaved] = useState(null) // { edits, templates } as published
  const [edits, setEdits] = useState(EMPTY)
  const [templates, setTemplates] = useState({})
  const [mode, setMode] = useState('edit')
  const [device, setDevice] = useState('desktop')
  const [path, setPath] = useState('/')
  const [framePath, setFramePath] = useState('/')
  const [publishing, setPublishing] = useState(false)

  // The website's address: its own subdomain, or ?branch= while developing.
  const site = useMemo(() => {
    try {
      const url = new URL(branch?.website_url || '', window.location.origin)
      return { origin: url.origin, query: url.search }
    } catch { return { origin: window.location.origin, query: '' } }
  }, [branch?.website_url])
  const devQuery = site.query || (branch?.website_url ? '' : `?branch=${branch?.id}`)
  const sameOrigin = site.origin === window.location.origin

  useEffect(() => {
    tenantAPI.getCurrent().then(({ data }) => {
      const settings = data.settings || {}
      const base = {
        edits: { ...EMPTY, ...(settings.site_edits || {}) },
        templates: Object.fromEntries(TEMPLATE_FIELDS.map(([, key]) => [key, settings[key] || 'default'])),
      }
      setSaved(base)
      setEdits({ texts: base.edits.texts || {}, hidden: base.edits.hidden || [], colors: base.edits.colors || {} })
      // Opened from a template's "Edit live" button: try that template.
      const fromLink = Object.fromEntries(TEMPLATE_FIELDS
        .map(([, key, , list]) => [key, params.get(key), list])
        .filter(([, value, list]) => value && list.some(tpl => tpl.value === value))
        .map(([key, value]) => [key, value]))
      setTemplates({ ...base.templates, ...fromLink })
    }).catch(() => toast.error(t('Could not load your website')))
  }, [])

  const dirty = saved && (JSON.stringify({ texts: edits.texts, hidden: edits.hidden, colors: edits.colors }) !== JSON.stringify({ texts: saved.edits.texts || {}, hidden: saved.edits.hidden || [], colors: saved.edits.colors || {} })
    || JSON.stringify(templates) !== JSON.stringify(saved.templates))

  // Show the draft in the frame.
  const send = message => frameRef.current?.contentWindow?.postMessage(message, site.origin)
  const sendPreview = () => {
    send({ type: 'se:preview', settings: { site_edits: edits, ...templates } })
    send({ type: 'se:mode', mode })
  }
  useEffect(() => { if (saved) sendPreview() }, [edits, templates, saved])
  useEffect(() => { send({ type: 'se:mode', mode }) }, [mode])

  useEffect(() => {
    const onMessage = e => {
      if (e.origin !== site.origin || e.source !== frameRef.current?.contentWindow) return
      const msg = e.data || {}
      if (msg.type === 'se:ready') { setPath(msg.path || '/'); sendPreviewRef.current() }
      if (msg.type === 'se:text' && typeof msg.original === 'string') {
        const key = `${msg.lang}|${msg.original}`
        setEdits(d => {
          const texts = { ...d.texts }
          if (!msg.value || msg.value === msg.original) delete texts[key]
          else texts[key] = msg.value
          return { ...d, texts }
        })
      }
      if (msg.type === 'se:hide' && msg.item) setEdits(d => (d.hidden.some(h => sameItem(h, msg.item)) ? d : { ...d, hidden: [...d.hidden, msg.item] }))
      if (msg.type === 'se:show' && msg.item) setEdits(d => ({ ...d, hidden: d.hidden.filter(h => !sameItem(h, msg.item)) }))
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [site.origin])
  const sendPreviewRef = useRef(sendPreview)
  sendPreviewRef.current = sendPreview

  // Leaving with unpublished changes asks first.
  useEffect(() => {
    if (!dirty) return
    const warn = e => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  const publish = async () => {
    setPublishing(true)
    try {
      const { data } = await themesAPI.saveSiteEdits(edits)
      const changedTemplates = Object.fromEntries(Object.entries(templates).filter(([key, value]) => value !== saved.templates[key]))
      if (Object.keys(changedTemplates).length) await appSettingsAPI.updateSettings(changedTemplates)
      setSaved({ edits: { ...EMPTY, ...data.edits }, templates })
      toast.success(t('Your website is updated'))
    } catch (error) {
      toast.error(t(error.response?.data?.error || 'Could not save your changes'))
    } finally { setPublishing(false) }
  }
  const discard = () => {
    if (!saved || !window.confirm(t('Discard all changes you have not published?'))) return
    setEdits({ texts: saved.edits.texts || {}, hidden: saved.edits.hidden || [], colors: saved.edits.colors || {} })
    setTemplates(saved.templates)
  }
  const leave = () => { if (!dirty || window.confirm(t('Leave without publishing your changes?'))) navigate('/admin/website') }

  const pages = [
    ['/', 'Home page'],
    ...services.slice(1).map(id => [{ menu: '/menu', store: '/shop', bookings: '/book' }[id], { menu: 'Menu', store: 'Shop', bookings: 'Bookings' }[id]]),
    ...(services.includes('menu') || services.includes('store') ? [['/cart', 'Cart'], ['/checkout', 'Checkout'], ['/order-status', 'Track an order']] : []),
    ...(services.includes('menu') ? [['/reservations', 'Table reservations']] : []),
    ['/account', 'Customer account'],
  ]
  const textEntries = Object.entries(edits.texts)

  if (!saved) return <div className="fixed inset-0 z-[60] grid place-items-center bg-slate-100 text-slate-500">{t('Loading your website...')}</div>

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-slate-100">
      {/* Top bar */}
      <header className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-3 py-2">
        <button type="button" onClick={leave} className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100">
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> {t('Back')}
        </button>
        <span className="hidden text-sm font-semibold text-slate-900 sm:inline">{t('Website editor')}</span>
        <div className="flex rounded-lg bg-slate-100 p-0.5" role="group" aria-label={t('Mode')}>
          {[['edit', MousePointerClick, 'Edit'], ['browse', Eye, 'Browse']].map(([id, Icon, label]) => (
            <button key={id} type="button" onClick={() => setMode(id)} aria-pressed={mode === id}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${mode === id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}>
              <Icon className="h-4 w-4" />{t(label)}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <span className="sr-only sm:not-sr-only">{t('Page')}</span>
          <select value={pages.some(([p]) => p === path.split('?')[0]) ? path.split('?')[0] : ''} onChange={e => { setFramePath(e.target.value); setPath(e.target.value) }}
            className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm">
            {!pages.some(([p]) => p === path.split('?')[0]) && <option value="">{path.split('?')[0]}</option>}
            {pages.map(([p, label]) => <option key={p} value={p}>{t(label)}</option>)}
          </select>
        </label>
        <div className="flex rounded-lg bg-slate-100 p-0.5" role="group" aria-label={t('Screen size')}>
          {DEVICES.map(([id, Icon]) => (
            <button key={id} type="button" onClick={() => setDevice(id)} aria-pressed={device === id} aria-label={t(id === 'desktop' ? 'Computer' : id === 'tablet' ? 'Tablet' : 'Phone')}
              className={`rounded-md p-1.5 ${device === id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}><Icon className="h-4 w-4" /></button>
          ))}
        </div>
        <div className="ms-auto flex items-center gap-2">
          <a href={`${site.origin}${framePath === '/' ? '/' : framePath}${devQuery}`} target="_blank" rel="noreferrer" className="hidden items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-slate-600 hover:bg-slate-100 md:inline-flex">
            <ExternalLink className="h-4 w-4" /> {t('View website')}
          </a>
          <button type="button" onClick={discard} disabled={!dirty} className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-700 ring-1 ring-slate-300 hover:bg-slate-50 disabled:opacity-40">{t('Discard')}</button>
          <button type="button" onClick={publish} disabled={!dirty || publishing} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-700 px-4 py-1.5 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-40">
            <Check className="h-4 w-4" /> {publishing ? t('Publishing...') : t('Publish')}
          </button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* Preview */}
        <div className="flex min-h-[55vh] flex-1 justify-center overflow-auto p-3">
          {sameOrigin ? (
            <iframe ref={frameRef} name={EDITOR_FRAME} title={t('Website preview')} src={`${framePath}${devQuery}`} onLoad={sendPreview}
              className="h-full min-h-[50vh] rounded-xl bg-white shadow-lg ring-1 ring-slate-200 transition-[width]"
              style={{ width: DEVICES.find(d => d[0] === device)[2], maxWidth: '100%' }} />
          ) : (
            <div className="m-auto max-w-md rounded-xl bg-white p-6 text-center shadow">
              <p className="text-slate-700">{t('Open the editor from your store’s own address to edit it live.')}</p>
              <a href={`${site.origin}/admin/website/editor`} className="mt-4 inline-block rounded-lg bg-blue-700 px-4 py-2 font-semibold text-white">{t('Open my store’s editor')}</a>
            </div>
          )}
        </div>

        {/* Panel */}
        <aside className="w-full shrink-0 space-y-5 overflow-y-auto border-t border-slate-200 bg-white p-4 lg:w-[340px] lg:border-s lg:border-t-0">
          <div className="rounded-xl bg-blue-50 p-3 text-sm text-blue-900">
            {mode === 'edit'
              ? t('Click any text or item on your website: change its words, or hide it. Use "Select bigger area" to hide a whole card or section.')
              : t('Browse mode: click around your website like a customer. Switch back to Edit to change things.')}
          </div>

          <section>
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900"><LayoutTemplate className="h-4 w-4 text-blue-700" />{t('Layout')}</h2>
            <div className="space-y-2">
              {TEMPLATE_FIELDS.filter(([service]) => services.includes(service)).map(([, key, label, list]) => (
                <label key={key} className="block text-sm">
                  <span className="mb-1 block text-slate-600">{t(label)}</span>
                  <select value={templates[key] || 'default'} onChange={e => setTemplates(v => ({ ...v, [key]: e.target.value }))}
                    className="w-full rounded-lg border border-slate-300 px-2 py-1.5">
                    {list.map(tpl => <option key={tpl.value} value={tpl.value}>{t(tpl.label)}</option>)}
                  </select>
                </label>
              ))}
            </div>
          </section>

          <section>
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900"><Paintbrush className="h-4 w-4 text-blue-700" />{t('Colors')}</h2>
            <div className="space-y-2">
              {COLOR_FIELDS.map(([key, label, fallback]) => (
                <div key={key} className="flex items-center gap-2 text-sm">
                  <input type="color" value={edits.colors[key] || fallback} aria-label={t(label)}
                    onChange={e => setEdits(d => ({ ...d, colors: { ...d.colors, [key]: e.target.value } }))}
                    className="h-8 w-10 cursor-pointer rounded border border-slate-300" />
                  <span className="flex-1 text-slate-700">{t(label)}</span>
                  {edits.colors[key]
                    ? <button type="button" onClick={() => setEdits(d => { const colors = { ...d.colors }; delete colors[key]; return { ...d, colors } })} className="text-xs text-slate-500 hover:text-slate-800" aria-label={t('Use the template color')}><RotateCcw className="h-3.5 w-3.5" /></button>
                    : <span className="text-xs text-slate-400">{t('Template')}</span>}
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900"><Type className="h-4 w-4 text-blue-700" />{t('Changed texts')} <span className="text-slate-400">({textEntries.length})</span></h2>
            {textEntries.length === 0 ? <p className="text-xs text-slate-500">{t('None yet. Click a text on your website to change it.')}</p> : (
              <ul className="space-y-2">
                {textEntries.map(([key, value]) => {
                  const [lang, ...rest] = key.split('|')
                  return (
                    <li key={key} className="rounded-lg border border-slate-200 p-2 text-xs">
                      <div className="flex items-start gap-2">
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold uppercase text-slate-500">{lang}</span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-slate-400 line-through">{rest.join('|')}</p>
                          <p className="break-words text-slate-900">{value}</p>
                        </div>
                        <button type="button" onClick={() => setEdits(d => { const texts = { ...d.texts }; delete texts[key]; return { ...d, texts } })} aria-label={t('Undo')} className="text-slate-500 hover:text-slate-900"><Undo2 className="h-4 w-4" /></button>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>

          <section>
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900"><EyeOff className="h-4 w-4 text-blue-700" />{t('Hidden items')} <span className="text-slate-400">({edits.hidden.length})</span></h2>
            {edits.hidden.length === 0 ? <p className="text-xs text-slate-500">{t('None. Click an item and choose Hide to remove it from your website.')}</p> : (
              <ul className="space-y-2">
                {edits.hidden.map(item => (
                  <li key={`${item.tag}|${item.cls}|${item.text}|${item.src}`} className="flex items-center gap-2 rounded-lg border border-slate-200 p-2 text-xs">
                    <span className="min-w-0 flex-1 truncate text-slate-700">{item.label || item.tag}</span>
                    <button type="button" onClick={() => setEdits(d => ({ ...d, hidden: d.hidden.filter(h => !sameItem(h, item)) }))} className="font-medium text-blue-700 hover:underline">{t('Show')}</button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <p className="text-xs text-slate-500">
            {t('Product names, prices and photos are changed on the products page.')}{' '}
            <Link to={services.includes('store') ? '/admin/products' : services.includes('menu') ? '/admin/menu' : '/admin/services'} className="text-blue-700 hover:underline">{t('Open')}</Link>
          </p>
        </aside>
      </div>
    </div>
  )
}
